import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { nav, business } from "@/data/content";

export default function Header({ onQuote }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-pristine-aqua shadow-[0_4px_24px_rgba(15,58,112,0.06)] py-2.5"
          : "bg-transparent py-4"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 flex items-center justify-between">
        <a href="#home" aria-label="Pristine Water Systems home" data-testid="site-logo">
          <span className="font-heading font-semibold uppercase tracking-[0.18em] text-pristine-blue text-sm sm:text-base">
            Pristine Water Systems
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {nav.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm font-medium text-pristine-navy/80 hover:text-pristine-blue transition-colors"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={business.phoneHref}
            data-testid="header-call-cta"
            className="inline-flex items-center gap-2 text-sm font-semibold text-pristine-blue hover:text-pristine-aquadeep transition-colors"
          >
            <Phone className="h-4 w-4" />
            Call {business.phone}
          </a>
          <Button
            data-testid="header-quote-cta"
            onClick={onQuote}
            className="rounded-full bg-pristine-blue hover:bg-pristine-bluehover text-white px-6 h-11 font-semibold transition-transform hover:scale-[1.03]"
          >
            Request a Quote
          </Button>
        </div>

        <button
          className="lg:hidden text-pristine-blue p-2"
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-pristine-aqua mt-2.5" data-testid="mobile-menu">
          <div className="px-5 py-4 flex flex-col gap-1">
            {nav.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                className="py-3 text-base font-medium text-pristine-navy border-b border-pristine-aqua/60"
              >
                {n.label}
              </a>
            ))}
            <div className="flex gap-3 pt-4">
              <a href={business.phoneHref} className="flex-1">
                <Button variant="outline" className="w-full rounded-full border-pristine-blue text-pristine-blue">
                  <Phone className="h-4 w-4 mr-2" /> Call
                </Button>
              </a>
              <Button
                onClick={() => { setOpen(false); onQuote(); }}
                className="flex-1 rounded-full bg-pristine-blue text-white"
              >
                Request a Quote
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
